import React, { SyntheticEvent } from 'react'
import { Autocomplete, TextField, Box, FormControl, FormHelperText } from '@mui/material'
import { LegalAddress } from '../../interfaces/common/LegalAddress'
import { AddressResponse } from '../../interfaces/common/AddressResponse'
import { fetchAddresses } from '../../api/common/getAddresses'

const getAddressLabel = (option?: AddressResponse | LegalAddress | null) => {
  if (!option) {
    return ''
  }

  const address = 'address' in option ? option.address : 'adrese' in option ? option.adrese : ''
  const zipCode = 'zipCode' in option ? option.zipCode : 'zip' in option ? option.zip : ''

  if (!address) {
    return ''
  }

  return address + (zipCode ? `, ${zipCode}` : '')
}

interface Props {
  value: LegalAddress | null | undefined
  disabled?: boolean
  onValueChange: (
    event: SyntheticEvent<Element, Event>,
    newValue: LegalAddress | null,
    index?: number
  ) => void
  index?: number
  helperText?: string
  error?: boolean
}

export default function AddressDropDownList({
  onValueChange,
  disabled = false,
  value,
  helperText,
  error,
  index,
  ...props
}: Props) {
  const [inputValue, setInputValue] = React.useState('')
  const [options, setOptions] = React.useState<AddressResponse[]>([])
  const [isLoading, setIsLoading] = React.useState(false)

  const addressQueryParams = {
    limit: '100',
    search: inputValue,
    lks_koord: 'true',
    atvk: 'true',
    struct: 'true',
  }

  React.useEffect(() => {
    async function fetchData() {
      setIsLoading(true)

      fetchAddresses(addressQueryParams)
        .then((resp) => {
          setOptions(resp)
        })
        .catch((e) => {
          setOptions([])
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    const timeoutId = setTimeout(() => {
      fetchData()
    }, 500)

    return () => {
      clearTimeout(timeoutId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue])

  const handleAddressChangeMapping = (
    e: React.SyntheticEvent<Element, Event>,
    newValue: AddressResponse | null
  ) => {
    if (!newValue) {
      onValueChange(e, null, index)
      return
    }

    const mappedAddress: LegalAddress = {
      adrese: newValue.address || '',
      koord_y: newValue.lksCoordX || 0,
      koord_x: newValue.lksCoordY || 0,
      zip: newValue.zipCode || '',
      pagasts_nosaukums: newValue.pagName || '',
      pilseta_atvk: newValue.pilAtvk || '',
      code: newValue.code || 0,
      novads_nosaukums: newValue.novName || '',
      novads_atvk: newValue.novAtvk || '',
      pilseta_nosaukums: newValue.pilName || '',
      pagasts_atvk: newValue.pagAtvk || '',
      pareiza_adrese: newValue.irAdrese || false,
    }

    onValueChange(e, mappedAddress, index)
  }

  return (
    <FormControl
      variant="outlined"
      error={error}
      fullWidth
      sx={{
        flexGrow: 1,
        minWidth: 400,
      }}
      disabled={disabled}
    >
      <Autocomplete
        disabled={disabled}
        value={value}
        onChange={(e, newValue) => handleAddressChangeMapping(e, newValue)}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue)
        }}
        loading={isLoading}
        getOptionLabel={(option) => getAddressLabel(option)}
        options={options}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        filterOptions={(x) => x}
        sx={{ flexGrow: 1 }}
        noOptionsText={'Adrese netika atrasta'}
        renderOption={(props, jsonResults) => (
          <Box component="li" {...props} key={jsonResults.code}>
            {jsonResults.address}
          </Box>
        )}
        renderInput={(params) => (
          <TextField multiline {...params} sx={{ flexGrow: 1 }} size="small" />
        )}
      />
      {error && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
